/**
 * A vehicle's shape (ROUND4 §1, revised after FIELD measured the models). A vehicle is NOT a circle: a
 * locomotive is 9.8 m long and a staff car 4.4, and a 2.2 m circle in the middle of either one lets the
 * player stand where he can plainly see steel. The hull is a capsule - `VEHICLES.radius` about a spine of
 * `2 x halfLength` turned by the hull's `facing` - and every question the sim asks about a vehicle's body
 * (what stops it, who goes under it) is asked of that capsule.
 *
 * A leaf: geometry, tuning and types only, so `src/gen`'s validator can ask the same questions of a route
 * before the mission is ever played.
 */

import { cos, sin } from '../../math';
import type { Collider } from '../../props';
import { VEHICLES } from '../../tuning';
import type { EnemyKind } from '../../types';

const HALF_LENGTH: Partial<Record<EnemyKind, number>> = VEHICLES.halfLength;
const WRECK_RADIUS: Partial<Record<EnemyKind, number>> = VEHICLES.wreckRadius;
const WRECK_HEIGHT: Partial<Record<EnemyKind, number>> = VEHICLES.wreckHeight;

/** Half the hull's length, nose to centre. A kind with no figure is a lorry. */
export const hullHalfLength = (kind: EnemyKind): number => HALF_LENGTH[kind] ?? VEHICLES.halfLength.truck;

/** How far the wreck MODEL's debris reaches: what a player sees, and therefore what stops him. */
export const wreckRadiusOf = (kind: EnemyKind): number => WRECK_RADIUS[kind] ?? VEHICLES.wreckRadius.truck;

/**
 * How tall the wreck MODEL stands: what a player sees, and therefore what his rounds hit. A locomotive's
 * hulk is 3.15 m and a burnt saloon 1.5 m; one figure for both put a metre of cover where there was none
 * and took a metre away where a player could see it standing.
 */
export const wreckHeightOf = (kind: EnemyKind): number => WRECK_HEIGHT[kind] ?? VEHICLES.wreckHeight.truck;

export interface Spine {
  ax: number;
  az: number;
  bx: number;
  bz: number;
}

/** The hull's spine, nose to tail: the line a capsule of `VEHICLES.radius` is swept along. */
export function hullSpine(kind: EnemyKind, x: number, z: number, facing: number): Spine {
  // The spine stops a radius short of each end, so the capsule's caps are the nose and the tail.
  const reach = Math.max(0, hullHalfLength(kind) - VEHICLES.radius);
  const dx = sin(facing) * reach;
  const dz = cos(facing) * reach;
  return { ax: x - dx, az: z - dz, bx: x + dx, bz: z + dz };
}

/** Distance from a point to the hull's spine: under `VEHICLES.radius` is inside the hull. */
export function distanceToSpine(s: Spine, px: number, pz: number): number {
  const vx = s.bx - s.ax;
  const vz = s.bz - s.az;
  const len2 = vx * vx + vz * vz;
  const t = len2 === 0 ? 0 : Math.min(1, Math.max(0, ((px - s.ax) * vx + (pz - s.az) * vz) / len2));
  const dx = px - (s.ax + vx * t);
  const dz = pz - (s.az + vz * t);
  return Math.sqrt(dx * dx + dz * dz);
}

/** Is this collider's footprint inside a circle of `r` at (x, z)? */
function touches(c: Collider, x: number, z: number, r: number): boolean {
  if (c.shape === 'circle') {
    const reach = c.r + r;
    return (x - c.x) * (x - c.x) + (z - c.z) * (z - c.z) < reach * reach;
  }
  const nx = Math.min(c.x + c.hx, Math.max(c.x - c.hx, x));
  const nz = Math.min(c.z + c.hz, Math.max(c.z - c.hz, z));
  return (x - nx) * (x - nx) + (z - nz) * (z - nz) < r * r;
}

/**
 * Is anything that stops three tonnes of lorry inside the hull standing here? Wire, sandbags, crates and
 * hedges are `crushable` and are not: it goes over them. Walls, buildings, trees and other wrecks are.
 * The capsule is answered by sampling its spine a radius at a time, which never misses a footprint.
 */
export function hullBlocked(
  colliders: readonly Collider[],
  kind: EnemyKind,
  x: number,
  z: number,
  facing: number,
): boolean {
  const s = hullSpine(kind, x, z, facing);
  const span = Math.sqrt((s.bx - s.ax) * (s.bx - s.ax) + (s.bz - s.az) * (s.bz - s.az));
  const steps = Math.max(1, Math.ceil(span / VEHICLES.radius));
  for (const c of colliders) {
    if (!c.blocksMove || c.crushable === true) continue;
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      if (touches(c, s.ax + (s.bx - s.ax) * t, s.az + (s.bz - s.az) * t, VEHICLES.radius)) return true;
    }
  }
  return false;
}
